import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Vessel {
  id: string;
  name: string;
  type: string;
  capacity_l: number | null;
  capacity_tons: number | null;
  is_active: boolean;
  org_id: string;
  facility_id: string | null;
  owner_client_id: string | null;
  manufacturer: string | null;
  sub_location: string | null;
  created_at: string;
  updated_at: string;
  vintrace_vessel_id: string | null;
}

export interface VesselWithRelations extends Vessel {
  facility: {
    id: string;
    code: string;
    name: string;
  } | null;
  owner_client: {
    id: string;
    name: string;
  } | null;
}

export interface CreateVesselInput {
  name: string;
  vessel_kind: string;
  vessel_subtypes?: string[];
  capacity_l?: number;
  capacity_tons?: number;
}

export interface UpdateVesselInput {
  name?: string;
  vessel_kind?: string;
  vessel_subtypes?: string[] | null;
  capacity_l?: number;
  capacity_tons?: number | null;
  preferred_capacity_unit?: string | null;
  facility_id?: string | null;
  owner_client_id?: string | null;
  manufacturer?: string | null;
}

export function useVessels(searchTerm?: string, typeFilter?: string, facilityFilter?: string) {
  return useQuery({
    queryKey: ["vessels", searchTerm, typeFilter, facilityFilter],
    queryFn: async () => {
      // Try query with relations first, fall back to simple query if facility table doesn't exist
      let query = supabase
        .from("vessel")
        .select(`
          *,
          facility:facility_id (
            id,
            code,
            name
          ),
          owner_client:owner_client_id (
            id,
            name
          )
        `)
        .neq("is_active", false)
        .order("name", { ascending: true });

      if (searchTerm) {
        query = query.ilike("name", `%${searchTerm}%`);
      }

      if (typeFilter) {
        query = query.eq("type", typeFilter);
      }

      if (facilityFilter && facilityFilter !== "all") {
        query = query.eq("facility_id", facilityFilter);
      }

      let { data, error } = await query;

      // If the query failed (likely because facility table doesn't exist), try without relations
      if (error) {
        console.warn("Vessel query with relations failed, falling back to simple query:", error.message);
        let simpleQuery = supabase
          .from("vessel")
          .select("*")
          .neq("is_active", false)
          .order("name", { ascending: true });

        if (searchTerm) {
          simpleQuery = simpleQuery.ilike("name", `%${searchTerm}%`);
        }

        if (typeFilter) {
          simpleQuery = simpleQuery.eq("type", typeFilter);
        }

        const simpleResult = await simpleQuery;
        if (simpleResult.error) throw simpleResult.error;

        // Add null relations for compatibility
        data = (simpleResult.data || []).map(v => ({
          ...v,
          facility: null,
          owner_client: null,
        }));
      }

      return data as VesselWithRelations[];
    },
    staleTime: 120000, // Cache vessels for 2 minutes - they rarely change
    gcTime: 600000, // Keep in cache for 10 minutes
    placeholderData: keepPreviousData,
  });
}

export function useArchivedVessels(searchTerm?: string, typeFilter?: string, facilityFilter?: string) {
  return useQuery({
    queryKey: ["archived-vessels", searchTerm, typeFilter, facilityFilter],
    queryFn: async () => {
      let query = supabase
        .from("vessel")
        .select(`
          *,
          facility:facility_id (
            id,
            code,
            name
          ),
          owner_client:owner_client_id (
            id,
            name
          )
        `)
        .eq("is_active", false)
        .order("name", { ascending: true });

      if (searchTerm) {
        query = query.ilike("name", `%${searchTerm}%`);
      }

      if (typeFilter) {
        query = query.eq("type", typeFilter);
      }

      if (facilityFilter && facilityFilter !== "all") {
        query = query.eq("facility_id", facilityFilter);
      }

      let { data, error } = await query;

      // Fallback if facility table doesn't exist
      if (error) {
        let simpleQuery = supabase
          .from("vessel")
          .select("*")
          .eq("is_active", false)
          .order("name", { ascending: true });

        if (searchTerm) {
          simpleQuery = simpleQuery.ilike("name", `%${searchTerm}%`);
        }

        if (typeFilter) {
          simpleQuery = simpleQuery.eq("type", typeFilter);
        }

        const simpleResult = await simpleQuery;
        if (simpleResult.error) throw simpleResult.error;

        data = (simpleResult.data || []).map(v => ({
          ...v,
          facility: null,
          owner_client: null,
        }));
      }

      return data as VesselWithRelations[];
    },
    staleTime: 120000,
    gcTime: 600000,
    placeholderData: keepPreviousData,
  });
}

export function useCreateVessel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateVesselInput) => {
      // First, get org_id from current user
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("vessel")
        .insert([{
          name: input.name,
          type: input.vessel_kind,          // keep type in sync for legacy queries
          vessel_kind: input.vessel_kind,
          vessel_subtypes: input.vessel_subtypes ?? [],
          capacity_l: input.capacity_l || null,
          capacity_tons: input.capacity_tons || null,
          org_id: userData.org_id,
        } as any])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vessels"] });
      toast({
        title: "Vessel created",
        description: "The vessel has been successfully created.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating vessel",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateVessel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateVesselInput }) => {
      const { data, error } = await supabase
        .from("vessel")
        .update(input)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vessels"] });
      queryClient.invalidateQueries({ queryKey: ["vessel-stats"] });
      toast({
        title: "Vessel updated",
        description: "The vessel has been successfully updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating vessel",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export interface BlockingAssignment {
  id: string;
  fill_date: string;
  wine_lot_code: string | null;
}

export interface VesselDependencies {
  canDelete: boolean;
  assignmentCount: number;
  message: string | null;
  assignments: BlockingAssignment[];
}

export function useVesselDependencies(vesselId: string | null) {
  return useQuery({
    queryKey: ["vessel-dependencies", vesselId],
    queryFn: async (): Promise<VesselDependencies> => {
      if (!vesselId) {
        return {
          canDelete: false,
          assignmentCount: 0,
          message: null,
          assignments: [],
        };
      }

      // Fetch vessel assignments with lot info
      const { data: assignmentData, error: assignmentError } = await supabase
        .from("vessel_assignment")
        .select(`
          id,
          fill_date,
          wine_lot (code)
        `)
        .eq("vessel_id", vesselId)
        .order("fill_date", { ascending: false })
        .limit(10);

      if (assignmentError) {
        console.error("Assignment query error:", assignmentError);
        throw assignmentError;
      }

      // Transform assignments
      const assignments: BlockingAssignment[] = (assignmentData || []).map((a: any) => ({
        id: a.id,
        fill_date: a.fill_date,
        wine_lot_code: a.wine_lot?.code || null,
      }));

      const totalAssignments = assignments.length;
      const canDelete = totalAssignments === 0;

      const message = canDelete
        ? null
        : `Cannot delete: vessel has ${totalAssignments} lot assignment${totalAssignments !== 1 ? 's' : ''}`;

      return {
        canDelete,
        assignmentCount: totalAssignments,
        message,
        assignments,
      };
    },
    enabled: !!vesselId,
    staleTime: 30000,
  });
}

export function useDeleteVessel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // First, check for dependencies
      const { count: assignmentCount, error: assignmentError } = await supabase
        .from("vessel_assignment")
        .select("*", { count: "exact", head: true })
        .eq("vessel_id", id);

      if (assignmentError) throw assignmentError;

      const totalAssignments = assignmentCount || 0;

      if (totalAssignments > 0) {
        throw new Error(`Cannot delete vessel: it has ${totalAssignments} lot assignment${totalAssignments !== 1 ? 's' : ''}. Remove these first.`);
      }

      // Safe to delete
      const { error } = await supabase.from("vessel").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vessels"] });
      queryClient.invalidateQueries({ queryKey: ["vessel-dependencies"] });
      toast({
        title: "Vessel deleted",
        description: "The vessel has been successfully deleted.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting vessel",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Archive a vessel (soft delete)
 * Sets is_active = false
 */
export function useArchiveVessel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vesselId: string) => {
      const { data, error } = await supabase
        .from("vessel")
        .update({ is_active: false })
        .eq("id", vesselId)
        .select()
        .single();

      if (error) throw error;
      return data as Vessel;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["vessels"] });
      queryClient.invalidateQueries({ queryKey: ["archived-vessels"] });
      queryClient.invalidateQueries({ queryKey: ["vessel-stats"] });
      queryClient.invalidateQueries({ queryKey: ["vessel-dependencies"] });
      toast({
        title: "Vessel archived",
        description: `${data.name} has been archived and will no longer appear in active lists.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error archiving vessel",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Restore an archived vessel
 * Sets is_active = true
 */
export function useUnarchiveVessel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vesselId: string) => {
      const { data, error } = await supabase
        .from("vessel")
        .update({ is_active: true })
        .eq("id", vesselId)
        .select()
        .single();

      if (error) throw error;
      return data as Vessel;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["vessels"] });
      queryClient.invalidateQueries({ queryKey: ["archived-vessels"] });
      queryClient.invalidateQueries({ queryKey: ["vessel-stats"] });
      toast({
        title: "Vessel restored",
        description: `${data.name} has been restored and is now active.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error restoring vessel",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
